/**
 * Subscriptions sub-nav registry — THE single place to add a Subscriptions
 * sub-nav. The section tabs (components/sections.tsx), the routes (App.tsx), and
 * the provisionable page list all derive from this, so adding a sub-nav = add a
 * page file + one entry here (no hub-core files). Mirrors the analytics registry.
 *
 * ACCESS: every entry carries a `key` matching an entry in shared
 * `SUBSCRIPTION_PAGES` — add the key there too so the sub-nav is provisionable.
 * Grants are APP-ADMIN only (Revenue = financial). `admin: true` marks the
 * Access page (app-admin only; no per-page grant). `requiresAnalytics: true`
 * marks a sub-nav whose DATA comes from the analytics-service backend (needs
 * the separate `analytics` claim, i.e. `tools.analytics.enabled`) rather than
 * this tool's own Stripe/Piano data — hidden even from a subscriptions admin
 * who lacks that claim, since the underlying API call would just fail (see
 * the Audio entry below).
 */
import type { ReactNode } from 'react';
import { lazyPage } from '../routing/lazyPage';

// Pages are lazy-loaded (route-level code splitting, Oct 2026): add new ones
// as lazyPage(() => import('./X'), 'X'), NOT a static import — this registry
// is imported by the always-loaded nav, so a static import puts the page in
// the main bundle. See web/src/routing/lazyPage.tsx.
const SubscriptionAccessPage = lazyPage(() => import('./SubscriptionAccessPage'), 'SubscriptionAccessPage');
const AnalyticsAudioPage = lazyPage(() => import('../analytics/AnalyticsAudioPage'), 'AnalyticsAudioPage');
const SubscriptionsDashboardPage = lazyPage(() => import('./SubscriptionsDashboardPage'), 'default');
const SubscriptionsHealthPage = lazyPage(() => import('./SubscriptionsHealthPage'), 'default');
const SubscriptionsInsightsPage = lazyPage(() => import('./SubscriptionsInsightsPage'), 'default');
const SubscriptionsDeepDivePage = lazyPage(() => import('./SubscriptionsDeepDivePage'), 'default');
const SubscriptionsChurnPage = lazyPage(() => import('./SubscriptionsChurnPage'), 'default');
const SubscriptionsChurnListPage = lazyPage(() => import('./SubscriptionsChurnListPage'), 'default');
const SubscriptionsStatsPage = lazyPage(() => import('./SubscriptionsStatsPage'), 'default');
const SubscriptionsConversionPage = lazyPage(() => import('./SubscriptionsConversionPage'), 'default');
const SubscriptionsUserPerformancePage = lazyPage(() => import('./SubscriptionsUserPerformancePage'), 'default');
const SubscriptionsTypesPage = lazyPage(() => import('./SubscriptionsTypesPage'), 'default');
const SubscriptionsPromotionsPage = lazyPage(() => import('./SubscriptionsPromotionsPage'), 'default');

export interface SubscriptionNavEntry {
  /** Route path under /subscriptions ('' = index). */
  path: string;
  /** Sub-nav tab label. */
  label: string;
  element: ReactNode;
  /** Provisioning key — matches shared SUBSCRIPTION_PAGES. */
  key: string;
  /** true = routed but NOT shown in the sub-nav (detail pages). */
  hidden?: boolean;
  /** true = APP-ADMIN-only Access page (no per-page grant). */
  admin?: boolean;
  /** true = also requires the `analytics` claim (tools.analytics.enabled) — see the file header. */
  requiresAnalytics?: boolean;
}

export const SUBSCRIPTION_NAV: SubscriptionNavEntry[] = [
  { path: '', label: 'Revenue', element: <SubscriptionsDashboardPage />, key: 'revenue' },
  // Plan types — subscriber-base overview per plan type (Oct 2026), from the
  // health snapshot's `typeMonths`.
  { path: 'types', label: 'Plan types', element: <SubscriptionsTypesPage />, key: 'types' },
  { path: 'deep-dive', label: 'Revenue deep dive', element: <SubscriptionsDeepDivePage />, key: 'deep-dive' },
  { path: 'churn', label: 'Churn deep dive', element: <SubscriptionsChurnPage />, key: 'churn' },
  // Churn list (Oct 2026): a filterable segment of churned subscribers (customer ids only).
  { path: 'churn-list', label: 'Churn list', element: <SubscriptionsChurnListPage />, key: 'churn-list' },
  { path: 'health', label: 'Health', element: <SubscriptionsHealthPage />, key: 'health' },
  { path: 'insights', label: 'Insights', element: <SubscriptionsInsightsPage />, key: 'insights' },
  // User Performance — "MyAD" sidebar engagement (Audio/Maps/Archive/Content/
  // Benefits), live BigQuery via analytics-service (userPerformance.js), same
  // subscriptions-claim gate as Conversion below — NO requiresAnalytics.
  { path: 'user-performance', label: 'User Performance', element: <SubscriptionsUserPerformancePage />, key: 'user-performance' },
  // Audio is an Analytics (BigQuery web-traffic) dashboard, reused as-is here —
  // moved off its own Analytics sidebar entry (31 Aug/1 Sep 2026). Same
  // component, same analytics-service data; only the nav location changed.
  { path: 'audio', label: 'Audio', element: <AnalyticsAudioPage />, key: 'audio', requiresAnalytics: true },
  // Stats/Conversion — BigQuery via analytics-service, gated on the
  // `subscriptions` claim (not `analytics`), so NO requiresAnalytics — see
  // analytics-service/subscriptions.js (Conversion moved here Sep 2026;
  // Stats itself still queries hub-side via bqQuery(), pre-migration).
  { path: 'stats', label: 'Stats', element: <SubscriptionsStatsPage />, key: 'stats' },
  { path: 'conversion', label: 'Conversion', element: <SubscriptionsConversionPage />, key: 'conversion' },
  { path: 'promotions', label: 'Promotions', element: <SubscriptionsPromotionsPage />, key: 'promotions' },
  // ↑ add new sub-navs above here ↑
  { path: 'access', label: 'Access', element: <SubscriptionAccessPage />, key: 'access', admin: true },
];
